/**
 * QuietHeroCard — the calm left-hero card shown on the Overview tab when NO child
 * currently has a session in progress (the common case). It balances the wallet
 * card in the hero row so the layout never reads as lopsided, and surfaces the
 * most-recently-active child when known.
 *
 * Deliberately avoids the live card's "… is in a session right now" phrasing so it
 * never collides with the in-progress assertions in the QA tests.
 *
 * Redesign (2026-06): new component for the parent dashboard redesign.
 */

export interface QuietHeroCardProps {
  /** Most-recently-active child + formatted date, or null when there's no history. */
  lastActive: { childName: string; date: string } | null;
}

export function QuietHeroCard({ lastActive }: QuietHeroCardProps) {
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-otter-sand bg-white/70 p-6 text-center shadow-sm">
      <span
        aria-hidden="true"
        className="mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-otter-cream text-2xl"
      >
        🦦
      </span>
      <p className="font-display text-lg font-semibold text-otter-earth-800">
        No live sessions right now
      </p>
      {lastActive ? (
        <p className="mt-1 text-sm text-gray-500">
          Last active:{' '}
          <span className="font-semibold text-otter-earth-700">{lastActive.childName}</span> ·{' '}
          {lastActive.date}
        </p>
      ) : (
        <p className="mt-1 max-w-xs text-sm text-gray-500">
          You&apos;ll see live progress here whenever a learner starts a session.
        </p>
      )}
    </div>
  );
}
